import React from "react";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import useSubscriptions from "@/hooks/useSubscriptions";
import IntegrationNotEnabled from "./IntegrationNotEnabled";

/**
 * Gates an integration config page behind a v20 AdminFeatures flag.
 * When the flag is false the child component never mounts — no API calls fire.
 *
 * Usage in Routes.tsx:
 *   <GatedAdminFeature flagKey="enableHubSpot" name="HubSpot CRM">
 *     <HubSpot />
 *   </GatedAdminFeature>
 */
export function GatedAdminFeature({
  flagKey,
  name,
  description,
  children
}: {
  flagKey: keyof ReturnType<typeof useAdminFeatures>;
  name: string;
  description?: string;
  children: React.ReactNode;
}) {
  const features = useAdminFeatures();
  if (!features[flagKey]) {
    return <IntegrationNotEnabled name={name} description={description} />;
  }
  return <>{children}</>;
}

/**
 * Gates an integration config page behind a v10 subscription flag.
 * When the flag is false the child component never mounts — no API calls fire.
 *
 * Usage in Routes.tsx:
 *   <GatedSubscriptionFeature flagKey="enableFormstack" name="Formstack">
 *     <Formstack />
 *   </GatedSubscriptionFeature>
 */
export function GatedSubscriptionFeature({
  flagKey,
  name,
  description,
  children
}: {
  flagKey: string;
  name: string;
  description?: string;
  children: React.ReactNode;
}) {
  const { subscription, loading } = useSubscriptions();

  // While loading, render nothing — avoids flash of "not enabled" on first load
  if (loading) return null;

  if (!subscription?.[flagKey as keyof typeof subscription]) {
    return <IntegrationNotEnabled name={name} description={description} />;
  }
  return <>{children}</>;
}
